import { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames/bind';
import styles from './Main.module.css';

import {
  useGetDashboardQuery,
  useGetMainMutation,
} from '../../../services/main';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';

import { menuChanged } from '../../../features/infoSlice';
import { setCredentials } from '../../../features/authSlice';
import { selectLibOptions } from '../../../features/librarySlice';
import { selectShareOptions } from '../../../features/shareSlice';

import Container from '../../../components/Container';
import TopArea from '../../../components/TopArea';
import { Section } from '../../../components/Section';
import Footer from './Footer';

import {
  CARD_CONFIGS,
  ERROR_CODES,
  ALL,
  IS_SHOW_EVENT_MODAL,
  MAIN,
  MAIN_SECTION_LIST,
  SCHEDULED_DELETION_DATE,
  STUDENT_MAIN_DASHBOARD_TITLE,
  TEACHER_MAIN_DASHBOARD_TITLE,
} from '../../../constants';
import {
  modalHasOpenedChanged,
  selectIsMainListChanged,
  selectModals,
  selectNotifications,
  setIsListChanged,
} from '../../../features/mainSlice';
import {
  studentCountChanged,
  studentMaxStudentChanged,
} from '../../../features/classroomSlice';
import useModal from '../../../hooks/useModal';
import { useGetStorageRemovalListQuery } from '../../../services/membership';
import { formatDate } from '../../../utils/date';
import { getCookie } from '../../../utils/cookie';
import { getIsTimeInRange, isSmallScreenMediaQuery } from '../../../utils/util';
import {
  MainResponse,
  MainRequestBody,
  MainSections,
} from '../../../types/main';

import { FullDashboard } from '../../../components/Dashboard';
import { ActivityCardData } from '../../../types/dashboard';
import { formatCount, makeCard, makeEmptyCards } from '../../../utils/dashboard';

export default function Main() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const [getMain] = useGetMainMutation();
  const [dashboardSeq, setDashboardSeq] = useState<number | undefined>();
  const [selectedGroup, setSelectedGroup] = useState<string>(ALL);
  const { data: dashboardData } = useGetDashboardQuery(dashboardSeq);
  const { data: storageRemovalInfo, isLoading: isStorageRemovalInfoLoading } =
    useGetStorageRemovalListQuery();
  const [data, setData] = useState<MainResponse | null>(null);

  const { sortType: libSortType, sortOrder: libSortOrder } =
    useAppSelector(selectLibOptions);
  const { sortType: shareSortType, sortOrder: shareSortOrder } =
    useAppSelector(selectShareOptions);
  const isListChanged = useAppSelector(selectIsMainListChanged);
  const notifications = useAppSelector(selectNotifications);
  const exceededStorageModal = useAppSelector(selectModals)?.find(
    ({ name }) => name === 'exceededStorage'
  );

  const isExceededStorage = !!notifications?.find(
    ({ code }) => code === ERROR_CODES.STORAGE_EXCEEDED
  );

  const { toggleModal } = useModal();

  const sectionEntries = useMemo(() => {
    if (!data) return [];

    const allSections = Object.entries(MAIN_SECTION_LIST);

    if (data.user.userType === 2) {
      return allSections;
    } else {
      return allSections.reverse();
    }
  }, [data]);

  const body = useMemo<MainRequestBody>(
    () => ({
      personal: { sortBy: libSortType, sortDir: libSortOrder },
      share: { sortBy: shareSortType, sortDir: shareSortOrder },
    }),
    [libSortType, libSortOrder, shareSortType, shareSortOrder]
  );

  useEffect(() => {
    if (
      isExceededStorage &&
      storageRemovalInfo &&
      !exceededStorageModal?.hasOpened
    ) {
      toggleModal({
        id: 'exceeded-storage',
        type: 'confirm',
        isOpen: true,
        data: {
          addedText: `<br><br>${SCHEDULED_DELETION_DATE} : ${formatDate(
            storageRemovalInfo.removeDate
          )}`,
        },
      });
      dispatch(
        modalHasOpenedChanged({ name: 'exceededStorage', hasOpened: true })
      );
    }
  }, [
    isExceededStorage,
    storageRemovalInfo,
    exceededStorageModal,
    toggleModal,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(menuChanged('main'));

    const fetchMainData = async () => {
      try {
        const res = await getMain(body);

        if (!('data' in res)) {
          console.warn('No data received from getMain');
          return;
        }

        const { user, maxStudent, countStudent, membershipInfo } = res.data;
        setData(res.data);
        dispatch(
          setCredentials({
            user: {
              email: user.email,
              alias: user.alias,
              userId: user.userId,
              type: user.userType,
              membershipInfo: membershipInfo,
            },
          })
        );
        dispatch(setIsListChanged(false));
        dispatch(studentMaxStudentChanged(maxStudent));
        dispatch(studentCountChanged(countStudent));
      } catch (error) {
        console.error('Failed to get main', error);
      }
    };

    fetchMainData();
  }, [getMain, body, dispatch, isListChanged]);

  useEffect(() => {
    if (
      IS_SHOW_EVENT_MODAL &&
      getIsTimeInRange() &&
      getCookie('hideEventModal') !== 'true' &&
      !isSmallScreenMediaQuery
    ) {
      toggleModal({ type: 'component', id: 'event', isOpen: true });
    }
  }, [toggleModal]);

  const activityCards: ActivityCardData[] = useMemo(() => {
    if (!dashboardData) {
      return makeEmptyCards();
    }

    const {
      lastStudy,
      lastAttendance,
      keywordList,
      annotationCount,
      contentCount,
      totalTime,
      timeZone,
      weekZone,
      dailyAvg,
      attentionTime,
      noteTime,
    } = dashboardData;

    const keywordValue = keywordList.length ? keywordList[0] : '-';

    const cardValues: Record<string, { value: string; coverImage?: string }> = {
      'last-learning': {
        value: lastStudy?.title || lastStudy?.summary || '-',
        coverImage: lastStudy?.coverFilePath,
      },
      'last-date': { value: lastAttendance || '-' },
      keyword: { value: keywordValue },
      tool: { value: formatCount(annotationCount) },
      content: { value: formatCount(contentCount) },
      'total-time': { value: totalTime || '-' },
      'time-range': { value: timeZone || '-' },
      day: { value: weekZone || '-' },
      book: { value: dailyAvg || '-' },
      focus: { value: attentionTime || '-' },
      note: { value: noteTime || '-' },
    };

    return CARD_CONFIGS.map(({ id, title }) => {
      const { value, coverImage } = cardValues[id];
      return makeCard(id, title, value, coverImage);
    });
  }, [dashboardData]);

  const handleGroupChange = (groupName: string, groupIndex: number) => {
    setSelectedGroup(groupName);
    const seq =
      groupIndex > 0 ? data?.groupList[groupIndex - 1]?.seq : undefined;
    setDashboardSeq(seq);
  };

  if (!data || isStorageRemovalInfoLoading) return null;

  const dashboardTitle = (
    data.user.userType === 1
      ? TEACHER_MAIN_DASHBOARD_TITLE
      : STUDENT_MAIN_DASHBOARD_TITLE
  ).replace('{alias}', data.user.alias);

  return (
    <Container>
      <TopArea title={MAIN} />
      <FullDashboard
        title={dashboardTitle}
        cards={activityCards}
        groupList={data.groupList}
        selectedGroup={selectedGroup}
        className="learning-activity"
        onGroupChange={handleGroupChange}
      />
      <div className={cx('content')}>
        {sectionEntries.map(([key, value]) => {
          const sectionKey = key as keyof MainSections;
          const sectionData = data[sectionKey];

          return (
            <Section key={key} name={key} title={value} data={sectionData} />
          );
        })}
      </div>
      <Footer />
    </Container>
  );
}
